import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { fmtMoney2 } from '../lib/fmt';
import { fileToPdfMeta, openPdfFromApi } from '../lib/pdf';
import { MoneyInput } from './inputs/MoneyInput';
import {
  calcPayments,
  descSimilarity,
  extractPdfText,
  normalizeSku,
  parseMBCPedidoPDF,
} from '../lib/pdfParsers';
import type { ItemOut, PedidoIn, PedidoOut } from '../types/api';

interface PdfMeta {
  name: string;
  size: number;
  type: string;
  has_pdf?: boolean;
  data_b64?: string;
}

interface PdfImportSummary {
  numero: string | null;
  fornecedor: string | null;
  valorTotal: number | null;
  condicao: string | null;
  dataFaturamento: string | null;
  matchedSkus: string[];
  descMatchedSkus: string[];
  missingSkus: string[];
  totalSkusDetectados: number;
}

interface PoModalProps {
  initialSkus: string[];
  allItems: ItemOut[];
  onClose: () => void;
  onCreated?: (po: PedidoOut) => void;
  onDeleted?: (id: number) => void;
  existingPedido?: PedidoOut | null;
}

type Scope = 'sug' | 'all' | 'sel' | 'pdf';

function formatDateLabel(dateIso: string | null) {
  if (!dateIso) return null;
  const date = new Date(`${dateIso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? dateIso : date.toLocaleDateString('pt-BR');
}



export function PoModal({
  initialSkus,
  allItems,
  onClose,
  onCreated,
  onDeleted,
  existingPedido,
}: PoModalProps) {
  const qc = useQueryClient();
  const isEditing = Boolean(existingPedido);

  const [numero, setNumero] = useState(existingPedido?.numero ?? '');
  const [fornecedor, setFornecedor] = useState(existingPedido?.fornecedor ?? '');
  const [valorTotal, setValorTotal] = useState<number>(existingPedido ? Number(existingPedido.valor_total) || 0 : 0);
  const [dataFaturamento, setDataFaturamento] = useState(
    existingPedido?.data_faturamento ? existingPedido.data_faturamento.slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [condicao, setCondicao] = useState(existingPedido?.condicao ?? '30,60,90');
  const [observacoes, setObservacoes] = useState(existingPedido?.observacoes ?? '');
  const [pdfMeta, setPdfMeta] = useState<PdfMeta | null>(existingPedido?.pdf_meta ?? null);
  const [pdfError, setPdfError] = useState('');
  const [pdfNotice, setPdfNotice] = useState('');
  const [pdfImportSummary, setPdfImportSummary] = useState<PdfImportSummary | null>(null);
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(
    new Set(existingPedido?.skus_json?.length ? existingPedido.skus_json : initialSkus),
  );
  const [search, setSearch] = useState('');
  const [filtMarca, setFiltMarca] = useState('');
  const [scope, setScope] = useState<Scope>('sug');

  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing || initialSkus.length === 0) return;
    const first = allItems.find(i => initialSkus.includes(i.sku));
    if ((first?.workflow_fornecedor ?? first?.fornecedor) && !fornecedor) {
      setFornecedor(first.workflow_fornecedor ?? first.fornecedor ?? '');
    }
    const est = allItems
      .filter(i => initialSkus.includes(i.sku))
      .reduce((s, i) => s + Number(i.custo_medio) * (i.workflow_qtd ?? i.sugestao_compra), 0);
    if (est > 0 && !valorTotal) setValorTotal(est);
  }, [allItems, fornecedor, initialSkus, isEditing, valorTotal]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const marcas = useMemo(
    () => [...new Set(allItems.map(i => i.marca).filter(Boolean) as string[])].sort(),
    [allItems],
  );

  const selectedItems = useMemo(
    () => allItems.filter(item => selectedSkus.has(item.sku)),
    [allItems, selectedSkus],
  );

  const skuLookup = useMemo(() => {
    const map = new Map<string, string>();
    allItems.forEach(item => {
      map.set(item.sku, item.sku);
      map.set(normalizeSku(item.sku), item.sku);
    });
    return map;
  }, [allItems]);

  const visibleItems = useMemo(() => {
    let arr = allItems;
    if (scope === 'sug') arr = arr.filter(i => i.sugestao_compra > 0);
    if (scope === 'sel') arr = arr.filter(i => selectedSkus.has(i.sku));
    if (scope === 'pdf') arr = arr.filter(i =>
      pdfImportSummary?.matchedSkus.includes(i.sku) ||
      pdfImportSummary?.descMatchedSkus.includes(i.sku),
    );
    if (filtMarca) arr = arr.filter(i => i.marca === filtMarca);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(i => i.sku.toLowerCase().includes(q) || i.nome.toLowerCase().includes(q));
    }
    return [...arr]
      .sort((a, b) => {
        const aSelected = selectedSkus.has(a.sku) ? 1 : 0;
        const bSelected = selectedSkus.has(b.sku) ? 1 : 0;
        if (aSelected !== bSelected) return bSelected - aSelected;

        const aExact = pdfImportSummary?.matchedSkus.includes(a.sku) ? 1 : 0;
        const bExact = pdfImportSummary?.matchedSkus.includes(b.sku) ? 1 : 0;
        if (aExact !== bExact) return bExact - aExact;

        const aDesc = pdfImportSummary?.descMatchedSkus.includes(a.sku) ? 1 : 0;
        const bDesc = pdfImportSummary?.descMatchedSkus.includes(b.sku) ? 1 : 0;
        if (aDesc !== bDesc) return bDesc - aDesc;

        if (a.sugestao_compra !== b.sugestao_compra) return b.sugestao_compra - a.sugestao_compra;
        return a.nome.localeCompare(b.nome, 'pt-BR');
      })
      .slice(0, 300);
  }, [allItems, filtMarca, pdfImportSummary?.matchedSkus, pdfImportSummary?.descMatchedSkus, scope, search, selectedSkus]);

  const toggleSku = useCallback((sku: string) => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      next.has(sku) ? next.delete(sku) : next.add(sku);
      return next;
    });
  }, []);

  const markAllVisible = () => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      visibleItems.forEach(i => next.add(i.sku));
      return next;
    });
  };

  const clearSelection = () => setSelectedSkus(new Set());

  const parcelas = useMemo(
    () => calcPayments(valorTotal || 0, condicao, dataFaturamento),
    [condicao, dataFaturamento, valorTotal],
  );

  const saveMut = useMutation({
    mutationFn: (payload: PedidoIn) => {
      if (isEditing && existingPedido) {
        return api.put<PedidoOut>(`/pedidos/${existingPedido.id}`, payload).then(r => r.data);
      }
      return api.post<PedidoOut>('/pedidos', payload).then(r => r.data);
    },
    onSuccess: po => {
      qc.invalidateQueries({ queryKey: ['pedidos'] });
      qc.invalidateQueries({ queryKey: ['cronograma'] });
      qc.invalidateQueries({ queryKey: ['comprador-items'] });
      qc.invalidateQueries({ queryKey: ['kpis'] });
      onCreated?.(po);
      onClose();
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete(`/pedidos/${id}`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['pedidos'] });
      qc.invalidateQueries({ queryKey: ['cronograma'] });
      onDeleted?.(id);
      onClose();
    },
  });

  const handlePdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setPdfError('');
    setPdfNotice('');
    setPdfImportSummary(null);
    setPdfMeta(await fileToPdfMeta(f));

    const fileNumMatch = f.name.match(/\b(\d{4,8})\b/);
    if (fileNumMatch && !numero) setNumero(fileNumMatch[1].replace(/^0+/, '').padStart(6, '0'));

    try {
      const extracted = await extractPdfText(f);
      const parsed = parseMBCPedidoPDF(extracted);

      if (parsed.numero) setNumero(parsed.numero);
      if (parsed.fornecedor) setFornecedor(parsed.fornecedor);
      if (parsed.valorTotal != null && parsed.valorTotal > 0) setValorTotal(parsed.valorTotal);
      if (parsed.condicao) setCondicao(parsed.condicao);
      if (parsed.dataFaturamento) setDataFaturamento(parsed.dataFaturamento);

      const matched: string[] = [];
      const descMatched: string[] = [];
      const missing: string[] = [];

      setSelectedSkus(prev => {
        const next = new Set(prev);
        parsed.skus.forEach(rawSku => {
          // 1) Exact code match
          const realSku = skuLookup.get(rawSku) ?? skuLookup.get(normalizeSku(rawSku));
          if (realSku) {
            next.add(realSku);
            matched.push(realSku);
            return;
          }
          // 2) Description similarity fallback (threshold ≥ 0.45)
          // rawSku is already normalized (leading zeros stripped), matching skuDescs keys
          const pdfDesc = parsed.skuDescs?.[rawSku];
          if (pdfDesc) {
            let bestScore = 0;
            let bestSku = '';
            for (const item of allItems) {
              const score = descSimilarity(pdfDesc, item.nome);
              if (score > bestScore) { bestScore = score; bestSku = item.sku; }
            }
            if (bestScore >= 0.45 && bestSku) {
              next.add(bestSku);
              descMatched.push(bestSku);
              return;
            }
          }
          missing.push(rawSku);
        });
        return next;
      });

      setPdfImportSummary({
        numero: parsed.numero,
        fornecedor: parsed.fornecedor,
        valorTotal: parsed.valorTotal,
        condicao: parsed.condicao,
        dataFaturamento: parsed.dataFaturamento,
        matchedSkus: [...new Set(matched)],
        descMatchedSkus: [...new Set(descMatched)],
        missingSkus: missing,
        totalSkusDetectados: parsed.skus.length,
      });
      if (matched.length || descMatched.length) setScope('pdf');

      if (!parsed.numero && !parsed.fornecedor && parsed.skus.length === 0) {
        setPdfError('PDF anexado, mas o conteúdo não trouxe campos reconhecíveis. Complete manualmente.');
        return;
      }

      // Only warn about truly unmatched items — those where neither SKU code
      // nor description similarity found a match in the catalog.
      if (missing.length) {
        setPdfNotice(`${missing.length} item(ns) do PDF sem correspondência no catálogo. Eles foram mantidos apenas como aviso.`);
      }

      if (!window.pdfjsLib) {
        setPdfError('PDF anexado, mas a biblioteca de leitura não está disponível neste navegador.');
      }
    } catch {
      setPdfError('Não foi possível ler o conteúdo do PDF. O preenchimento manual continua disponível.');
    }
  };

  const handleOpenSavedPdf = async () => {
    if (!existingPedido) return;
    setPdfError('');
    try {
      await openPdfFromApi(`/pedidos/${existingPedido.id}/pdf`, `${existingPedido.numero}.pdf`);
    } catch (error) {
      setPdfError(error instanceof Error ? error.message : 'PDF não encontrado para este pedido.');
    }
  };

  const handleSubmit = () => {
    if (!numero.trim()) { alert('Informe o número do pedido.'); return; }
    if (!fornecedor.trim()) { alert('Informe o fornecedor.'); return; }
    if (!parcelas.length) { alert('Preencha valor, condição e data válidos para calcular o cronograma.'); return; }

    const vt = valorTotal;
    saveMut.mutate({
      numero: numero.trim(),
      fornecedor: fornecedor.trim(),
      valor_total: isNaN(vt) ? 0 : Math.round(vt * 100) / 100,
      condicao: condicao || undefined,
      data_faturamento: dataFaturamento || undefined,
      observacoes: observacoes || undefined,
      pdf_meta: pdfMeta ?? undefined,
      skus_json: [...selectedSkus],
    });
  };

  const handleDelete = () => {
    if (!existingPedido) return;
    if (!window.confirm('Excluir este pedido? O cronograma financeiro associado será removido.')) return;
    deleteMut.mutate(existingPedido.id);
  };

  const errorDetail = (saveMut.error as { response?: { data?: { detail?: string } } } | null)?.response?.data?.detail;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="absolute inset-0 grid place-items-end md:place-items-center p-2 md:p-4 pointer-events-none">
        <div className="card glow pointer-events-auto w-full max-w-4xl overflow-y-auto rounded-2xl p-4 md:p-6" style={{ maxHeight: '96vh' }}>
          <div className="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-extrabold text-white">📥 {isEditing ? 'Editar' : 'Importar'} Pedido de Compra</h2>
              <p className="mt-1 text-xs text-mbc-mute">
                Anexe o PDF da nota/pedido, preencha os campos e identifique os produtos do pedido. O cronograma é calculado automaticamente.
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-3xl leading-none text-mbc-mute hover:text-white">×</button>
          </div>

          <div className="mb-4">
            <label className="text-xs font-semibold uppercase tracking-wider text-mbc-mute">📎 PDF do pedido</label>
            <div className="mt-1 grid gap-2 sm:grid-cols-[1fr_auto]">
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-mbc-line py-3 text-sm text-mbc-soft transition hover:border-mbc-orange">
                <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={handlePdf} />
                <span>📎 Selecionar PDF para preenchimento automático</span>
              </label>
              {isEditing && existingPedido && (
                <button
                  type="button"
                  onClick={handleOpenSavedPdf}
                  disabled={!pdfMeta?.has_pdf}
                  title={pdfMeta?.has_pdf ? 'Abrir PDF salvo no pedido' : 'PDF não salvo para este pedido'}
                  className="rounded-xl border border-mbc-line bg-mbc-panel2 px-4 py-3 text-sm font-semibold text-mbc-bluel hover:border-mbc-blue hover:text-white disabled:cursor-not-allowed disabled:opacity-45"
                >
                  Ver PDF
                </button>
              )}
            </div>
            {pdfMeta ? (
              <p className="mt-2 text-xs text-emerald-400">
                ✓ {pdfMeta.name} · {(pdfMeta.size / 1024).toFixed(1)} KB
                {pdfImportSummary && (() => {
                  const exact = pdfImportSummary.matchedSkus.length;
                  const byDesc = pdfImportSummary.descMatchedSkus.length;
                  const total = pdfImportSummary.totalSkusDetectados;
                  return ` · ${exact + byDesc}/${total} SKUs encontrados no catálogo${byDesc ? ` (${byDesc} por descrição)` : ''}`;
                })()}
              </p>
            ) : (
              <p className="mt-2 text-xs text-mbc-mute">Nenhum arquivo anexado.</p>
            )}
            {pdfError && <p className="mt-1 text-xs text-amber-300">{pdfError}</p>}
            {pdfNotice && <p className="mt-1 text-xs text-sky-300">{pdfNotice}</p>}
            {pdfImportSummary && (
              <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-mbc-soft">
                <div className="mb-2 font-semibold text-emerald-300">Informações reconhecidas no PDF</div>
                <div className="grid gap-1 sm:grid-cols-2">
                  <span>Número: <span className="text-white">{pdfImportSummary.numero ?? 'não identificado'}</span></span>
                  <span>Fornecedor: <span className="text-white">{pdfImportSummary.fornecedor ?? 'não identificado'}</span></span>
                  <span>Valor total: <span className="text-white">{pdfImportSummary.valorTotal != null ? fmtMoney2(pdfImportSummary.valorTotal) : 'não identificado'}</span></span>
                  <span>Condição: <span className="text-white">{pdfImportSummary.condicao ?? 'não identificada'}</span></span>
                  <span>Data faturamento: <span className="text-white">{formatDateLabel(pdfImportSummary.dataFaturamento) ?? 'não identificada'}</span></span>
                  <span>
                    Produtos encontrados:{' '}
                    <span className="text-white">
                      {pdfImportSummary.matchedSkus.length + pdfImportSummary.descMatchedSkus.length}/{pdfImportSummary.totalSkusDetectados}
                    </span>
                    {pdfImportSummary.descMatchedSkus.length > 0 && (
                      <span className="ml-1 text-sky-300">({pdfImportSummary.descMatchedSkus.length} por descrição)</span>
                    )}
                  </span>
                </div>
                {pdfImportSummary.missingSkus.length > 0 && (
                  <p className="mt-2 text-amber-300">
                    SKUs não localizados no catálogo: {pdfImportSummary.missingSkus.join(', ')}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mb-4 grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs text-mbc-mute">Número do pedido *</label>
              <input value={numero} onChange={e => setNumero(e.target.value)} placeholder="ex: 019984" className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-mbc-mute">Fornecedor *</label>
              <input value={fornecedor} onChange={e => setFornecedor(e.target.value)} placeholder="ex: FRESNOMAQ" className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-mbc-mute">Valor total do pedido (R$) *</label>
              <MoneyInput value={valorTotal} onChange={setValorTotal} />
            </div>
            <div>
              <label className="text-xs text-mbc-mute">Data do faturamento *</label>
              <input type="date" value={dataFaturamento} onChange={e => setDataFaturamento(e.target.value)} className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-mbc-mute">Condição de pagamento</label>
              <input value={condicao} onChange={e => setCondicao(e.target.value)} placeholder="ex: 30,60,90 ou 28/56/84 ou À vista" className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none" />
              <p className="mt-1 text-[10px] text-mbc-mute">Prazos contados a partir da data do faturamento. Separe por vírgula ou barra.</p>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-mbc-mute">Observações</label>
              <textarea rows={2} value={observacoes} onChange={e => setObservacoes(e.target.value)} placeholder="ex: Frete CIF, agendar entrega via e-mail, etc." className="mt-1 w-full rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-2 text-sm text-white focus:border-mbc-orange focus:outline-none resize-none" />
            </div>
          </div>

          <div className="card mb-4 p-4" style={{ background: 'rgba(244,122,32,.05)', borderColor: 'rgba(244,122,32,.3)' }}>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-wider text-mbc-orangel">📦 Produtos/SKUs incluídos no pedido</div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-mbc-mute">{selectedSkus.size} selecionados</span>
                <button type="button" onClick={markAllVisible} className="rounded bg-mbc-blue/30 px-2 py-1 text-xs text-mbc-bluel hover:bg-mbc-blue/50">marcar todos visíveis</button>
                <button type="button" onClick={clearSelection} className="rounded bg-rose-500/20 px-2 py-1 text-xs text-rose-300 hover:bg-rose-500/30">limpar</button>
              </div>
            </div>

            {selectedItems.length > 0 && (
              <div className="mb-3 rounded-xl border border-mbc-orange/20 bg-mbc-orange/5 p-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-mbc-orangel">Selecionados no pedido</div>
                <div className="flex max-h-24 flex-wrap gap-2 overflow-auto pr-1">
                  {selectedItems.map(item => (
                    <button
                      key={item.sku}
                      type="button"
                      onClick={() => toggleSku(item.sku)}
                      className="rounded-full border border-mbc-orange/30 bg-mbc-panel px-2.5 py-1 text-left text-[11px] text-mbc-soft hover:border-mbc-orange hover:text-white"
                      title={item.nome}
                    >
                      <span className="font-mono text-mbc-bluel">{item.sku}</span> · {item.nome.slice(0, 42)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔎 buscar SKU/produto…" className="rounded-lg border border-mbc-line bg-mbc-panel2 px-3 py-1.5 text-xs text-white placeholder:text-mbc-mute focus:outline-none focus:border-mbc-orange" />
              <select value={filtMarca} onChange={e => setFiltMarca(e.target.value)} className="min-h-[38px] rounded-lg border border-mbc-line bg-mbc-panel2 px-2 py-1.5 text-xs text-mbc-soft focus:outline-none focus:border-mbc-orange">
                <option value="">Marca: todas</option>
                {marcas.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={scope} onChange={e => setScope(e.target.value as Scope)} className="min-h-[38px] rounded-lg border border-mbc-line bg-mbc-panel2 px-2 py-1.5 text-xs text-mbc-soft focus:outline-none focus:border-mbc-orange">
                <option value="sug">Apenas com sugestão de compra</option>
                <option value="all">Todos os SKUs</option>
                <option value="sel">Apenas selecionados</option>
                <option value="pdf">Apenas identificados no PDF</option>
              </select>
            </div>

            <div className="overflow-auto rounded-[10px]" style={{ maxHeight: 240, border: '1px solid #22324F', background: '#0E1A2E' }}>
              {visibleItems.length === 0 ? (
                <div className="py-6 text-center text-xs text-mbc-mute">Nenhum SKU com este filtro.</div>
              ) : (
                visibleItems.map(i => {
                  const checked = selectedSkus.has(i.sku);
                  const exactPdf = pdfImportSummary?.matchedSkus.includes(i.sku) ?? false;
                  const descPdf  = pdfImportSummary?.descMatchedSkus.includes(i.sku) ?? false;
                  const highlight = exactPdf ? 'ring-1 ring-emerald-500/40 bg-emerald-500/5'
                    : descPdf ? 'ring-1 ring-sky-500/40 bg-sky-500/5' : '';
                  return (
                    <label
                      key={i.sku}
                      className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-mbc-panel ${checked ? 'bg-mbc-orange/10' : ''} ${highlight}`}
                      style={{ borderBottom: '1px solid #18243c' }}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleSku(i.sku)} className="accent-mbc-orange shrink-0" />
                      <span className="w-16 shrink-0 font-mono text-xs text-mbc-bluel">{i.sku}</span>
                      <span className="flex-1 truncate text-xs text-mbc-soft" title={i.nome}>{i.nome.slice(0, 60)}</span>
                      <span className="shrink-0 text-[10px] text-mbc-mute">{i.marca ?? ''}</span>
                      {exactPdf && <span className="badge shrink-0 bg-emerald-500/20 text-emerald-300">PDF</span>}
                      {descPdf  && <span className="badge shrink-0 bg-sky-500/20 text-sky-300" title="Vinculado por similaridade de descrição">desc</span>}
                      {i.sugestao_compra > 0 && <span className="badge shrink-0 bg-mbc-orange/20 text-mbc-orangel">{i.sugestao_compra}</span>}
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <div className="card mb-4 p-4" style={{ background: 'rgba(10,77,158,.08)', borderColor: 'rgba(30,111,214,.4)' }}>
            <div className="mb-3 text-xs font-bold uppercase tracking-wider text-mbc-bluel">🗓️ Cronograma calculado</div>
            {parcelas.length === 0 ? (
              <p className="py-3 text-center text-xs text-mbc-mute">Preencha valor, condição e data para ver o cronograma.</p>
            ) : (
              <div className="overflow-auto">
                <table className="dt" style={{ width: '100%' }}>
                  <colgroup>
                    <col style={{ width: 60 }} />
                    <col style={{ width: 70 }} />
                    <col style={{ width: 150 }} />
                    <col style={{ width: 90 }} />
                    <col style={{ width: 55 }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="text-center">Parc.</th>
                      <th className="num">Prazo</th>
                      <th>Vencimento</th>
                      <th className="text-center">Semana</th>
                      <th className="text-center">Dia</th>
                      <th className="num">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parcelas.map(p => (
                      <tr key={p.n}>
                        <td className="text-center font-bold">{p.n}/{parcelas.length}</td>
                        <td className="num">{p.dias}d</td>
                        <td>{new Date(p.vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}<span className="ml-1 text-[11px] text-mbc-mute">{p.mes}</span></td>
                        <td className="text-center"><span className="badge bg-mbc-blue/25 text-mbc-bluel">Sem {p.semanaDoMes}</span></td>
                        <td className="text-center text-mbc-mute">{p.diaSemana}</td>
                        <td className="num">{fmtMoney2(p.valor)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid #22324F' }}>
                      <td colSpan={5} className="text-right text-mbc-mute text-xs">Total:</td>
                      <td className="num font-bold text-white">{fmtMoney2(parcelas.reduce((s, p) => s + p.valor, 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {(saveMut.isError || deleteMut.isError) && (
            <p className="mb-3 text-sm text-rose-300">
              {errorDetail ?? 'Erro ao salvar ou excluir pedido.'}
            </p>
          )}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            {isEditing && existingPedido && (
              <button type="button" onClick={handleDelete} disabled={deleteMut.isPending} className="rounded-xl bg-rose-500/20 px-4 py-2.5 text-rose-300 hover:bg-rose-500/30 disabled:opacity-40 w-full sm:mr-auto sm:w-auto">
                {deleteMut.isPending ? 'Excluindo…' : '🗑️ Excluir pedido'}
              </button>
            )}
            <button type="button" onClick={onClose} className="rounded-xl border border-mbc-line bg-mbc-panel2 px-5 py-2.5 text-mbc-soft hover:text-white w-full sm:w-auto">
              Cancelar
            </button>
            <button type="button" onClick={handleSubmit} disabled={saveMut.isPending} className="pill-btn rounded-xl bg-mbc-orange px-6 py-2.5 font-bold text-white shadow-lg hover:bg-mbc-orangel disabled:opacity-40 w-full sm:w-auto">
              {saveMut.isPending ? 'Salvando…' : '💾 Salvar pedido + cronograma financeiro'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
